import React, { useState, useMemo } from 'react';
import { RefreshCw, Plus } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../services/api';

/**
 * מקטע נפתח בלשונית "ניהול ערוצים": הערוצים שהחשבון של הבוט כבר חבר בהם ועדיין לא במעקב,
 * עם סימון והוספה מרוכזת. נטען רק כשפותחים אותו (קריאה לטלגרם לוקחת כמה שניות).
 */
// ברירות המחדל בשאלה "לאן לפרסם" - כמו בטופס "הוסף ערוץ"
const DEFAULT_SETTINGS = {
  enabled: true, publish_to_general: true, publish_to_phone: true, publish_to_personal: true,
  send_to_admin_only: true, send_videos_to_personal: true
};

export default function AccountChannelsPanel({ onAdded, toggles = [] }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState(new Set());
  const [query, setQuery] = useState('');
  const [adding, setAdding] = useState(false);
  const [asking, setAsking] = useState(false);  // שלב "לאן לפרסם" לפני ההוספה
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/channels/account', { timeout: 120000 });
      setData(res.data);
      setSelected(new Set());
    } catch (e) {
      setError(e.response?.data?.error || 'שגיאה בקריאת הערוצים מהחשבון');
    } finally {
      setLoading(false);
    }
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = data?.channels || [];
    if (!q) return list;
    return list.filter(c => c.title.toLowerCase().includes(q) || c.username.toLowerCase().includes(q));
  }, [data, query]);

  const allFilteredSelected = filtered.length > 0 && filtered.every(c => selected.has(c.username));

  const toggle = (username) => {
    const next = new Set(selected);
    next.has(username) ? next.delete(username) : next.add(username);
    setSelected(next);
  };

  const toggleAll = () => {
    const next = new Set(selected);
    filtered.forEach(c => (allFilteredSelected ? next.delete(c.username) : next.add(c.username)));
    setSelected(next);
  };

  const addSelected = async () => {
    const chosen = (data?.channels || []).filter(c => selected.has(c.username));
    if (chosen.length === 0) return;
    setAdding(true);
    try {
      const res = await api.post('/channels/bulk-add', {
        channels: chosen.map(c => ({ username: c.username, name: c.title })),
        settings
      });
      toast.success(`${res.data.added} ערוצים נוספו למעקב`);
      setData({ ...data, channels: data.channels.filter(c => !selected.has(c.username)),
                monitored_in_account: (data.monitored_in_account || 0) + res.data.added });
      setSelected(new Set());
      setAsking(false);
      onAdded && onAdded();
    } catch (e) {
      toast.error(e.response?.data?.error || 'שגיאה בהוספת הערוצים');
    } finally {
      setAdding(false);
    }
  };

  return (
    <details
      className="bg-white rounded-lg shadow border border-gray-200"
      onToggle={(e) => { if (e.currentTarget.open && !data && !loading) load(); }}
    >
      <summary className="px-4 py-3 text-sm font-medium text-gray-700 cursor-pointer">
        📥 ערוצים בחשבון הטלגרם שלא במעקב{data ? ` (${data.channels.length})` : ''}
      </summary>

      <div className="px-4 pb-4 space-y-3">
        {loading && <div className="text-sm text-gray-500 py-2">טוען את הערוצים מהחשבון... (עד דקה)</div>}

        {error && !loading && (
          <div className="flex items-center justify-between gap-2 text-sm text-red-600 bg-red-50 rounded p-2">
            <span>{error}</span>
            <button onClick={load} className="px-2 py-1 bg-white border border-red-200 rounded">נסה שוב</button>
          </div>
        )}

        {data && !loading && (
          <>
            <div className="text-xs text-gray-500">
              החשבון חבר ב-{data.total}/500 ערוצים וקבוצות · {data.monitored_in_account} כבר במעקב
              {(data.no_username > 0 || data.groups > 0) &&
                ` · ${data.no_username} ערוצים בלי @ ו-${data.groups} קבוצות לא מוצגים (הבוט לא יכול לעקוב אחריהם)`}
            </div>

            {data.channels.length === 0 ? (
              <div className="text-sm text-gray-600 py-2">כל ערוצי החשבון כבר במעקב.</div>
            ) : (
              <>
                <div className="flex flex-wrap items-center gap-2">
                  <input
                    type="text"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="חיפוש לפי שם או @"
                    className="flex-1 min-w-[160px] px-3 py-1.5 border border-gray-300 rounded-lg text-sm"
                  />
                  <button onClick={toggleAll} className="px-3 py-1.5 text-sm bg-gray-100 rounded-lg hover:bg-gray-200">
                    {allFilteredSelected ? 'בטל סימון' : `סמן הכל (${filtered.length})`}
                  </button>
                  <button onClick={load} title="רענון" className="p-2 bg-gray-100 rounded-lg hover:bg-gray-200">
                    <RefreshCw className="w-4 h-4" />
                  </button>
                </div>

                <ul className="max-h-80 overflow-y-auto border border-gray-100 rounded-lg divide-y divide-gray-100">
                  {filtered.map(c => (
                    <li key={c.username}>
                      <label className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-gray-50">
                        <input
                          type="checkbox"
                          checked={selected.has(c.username)}
                          onChange={() => toggle(c.username)}
                          className="w-4 h-4"
                        />
                        <span className="flex-1 min-w-0">
                          <span className="block text-sm text-gray-900 truncate">{c.title}</span>
                          <a
                            href={`https://t.me/${c.username}`}
                            target="_blank"
                            rel="noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="text-xs text-blue-600"
                            dir="ltr"
                          >
                            @{c.username}
                          </a>
                        </span>
                        {c.last_post && (
                          <span className="text-xs text-gray-400 whitespace-nowrap">
                            {new Date(c.last_post).toLocaleDateString('he-IL')}
                          </span>
                        )}
                      </label>
                    </li>
                  ))}
                  {filtered.length === 0 && <li className="px-3 py-2 text-sm text-gray-500">אין תוצאות</li>}
                </ul>

                {!asking ? (
                  <div className="flex justify-end">
                    <button
                      onClick={() => setAsking(true)}
                      disabled={selected.size === 0}
                      className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                    >
                      <Plus className="w-4 h-4 ml-1" />
                      הוסף {selected.size} למעקב...
                    </button>
                  </div>
                ) : (
                  <div className="border border-blue-200 bg-blue-50 rounded-lg p-3 space-y-3">
                    <div className="text-sm font-medium text-gray-800">
                      לאן לפרסם את {selected.size} הערוצים? (אפשר לשנות אחר כך לכל ערוץ בטבלה)
                    </div>
                    <div className="flex flex-wrap gap-x-4 gap-y-2">
                      {[{ key: 'enabled', label: 'פעיל', title: 'הבוט מעבד הודעות מהערוץ' }, ...toggles].map(t => (
                        <label key={t.key} className="flex items-center gap-1.5 text-sm cursor-pointer" title={t.title}>
                          <input
                            type="checkbox"
                            checked={!!settings[t.key]}
                            onChange={(e) => setSettings({ ...settings, [t.key]: e.target.checked })}
                            className="w-4 h-4"
                          />
                          {t.icon ? `${t.icon} ` : ''}{t.label}
                        </label>
                      ))}
                    </div>
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <span className="text-xs text-gray-500">הבוט יתחיל לקבל מהם הודעות תוך דקה.</span>
                      <div className="flex gap-2">
                        <button
                          onClick={() => setAsking(false)}
                          className="px-3 py-2 text-sm bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                        >
                          ביטול
                        </button>
                        <button
                          onClick={addSelected}
                          disabled={selected.size === 0 || adding}
                          className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                        >
                          {adding ? 'מוסיף...' : `אישור והוספת ${selected.size}`}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>
    </details>
  );
}
